import React, { useState, useEffect } from "react";
import { View, TextInput, Pressable } from "react-native-web";
import { useNavigate, useLocation } from "react-router-dom";
import CustomText from "../components/atoms/CustomText";
import Button from "../components/common/Button";
import { colors, spacing, typography } from "../utils/styling";
import { useSetPassword } from "../lib/auth";
import SvgEye from "../components/icons/SvgEye";
import { useResponsive } from "../hooks/useResponsive";

interface UrlParams {
  access_token?: string;
  email?: string;
  type?: string;
  token?: string;
}

// Helper function to parse hash parameters
const parseHash = (hash: string): UrlParams => {
  console.log("🔍 Parsing URL hash:", hash);
  if (!hash) {
    console.log("❌ No hash found in URL");
    return {};
  }

  const params = new URLSearchParams(hash.replace("#", ""));
  const result = {
    access_token: params.get("access_token") || undefined,
    type: params.get("type") || undefined,
    token: params.get("token") || undefined,
  };
  console.log("📦 Parsed URL parameters:", result);
  return result;
};

// Helper function to decode JWT and get email
const getEmailFromToken = (token: string): string | null => {
  console.log("🔑 Attempting to decode token:", token);
  try {
    const base64Url = token.split(".")[1];
    console.log("📄 Base64URL part:", base64Url);

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    console.log("📄 Cleaned Base64:", base64);

    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    console.log("📄 Decoded JSON payload:", jsonPayload);

    const payload = JSON.parse(jsonPayload);
    console.log("📧 Found email in payload:", payload.email);
    return payload.email || null;
  } catch (e) {
    console.error("❌ Error decoding token:", e);
    return null;
  }
};

export default function SetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutateAsync: setPassword } = useSetPassword();
  const { isMobile } = useResponsive();

  const [password, setPasswordValue] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [linkAccessToken, setLinkAccessToken] = useState<string | null>(null);
  const [hasProcessedHash, setHasProcessedHash] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Only process the hash on first mount
    if (hasProcessedHash) return;

    console.log("🔄 Processing URL hash on first mount");
    console.log("📍 Current location:", location);

    // Parse the URL hash
    const { access_token, type, token: urlToken } = parseHash(location.hash);
    console.log("🎫 Parsed token:", access_token);
    console.log("📝 Link type:", type);
    console.log("🔑 URL token:", urlToken);

    // Handle both invite links and password reset links
    if (access_token) {
      console.log("✅ Valid link detected");
      setLinkAccessToken(access_token);
      const emailFromToken = getEmailFromToken(access_token);
      if (emailFromToken) {
        console.log("✅ Successfully extracted email:", emailFromToken);
        setEmail(emailFromToken);
      } else {
        console.error("❌ Failed to extract email from token");
        setError("Invalid link");
      }
    } else if (urlToken) {
      // Handle password reset links that have a separate token
      console.log("✅ Password reset link detected");
      setToken(urlToken);
      // Try to get email from URL params or query string
      const urlParams = new URLSearchParams(location.search);
      const emailFromUrl = urlParams.get("email");
      if (emailFromUrl) {
        setEmail(emailFromUrl);
      } else {
        // If no email in URL, show error - we need email for password reset
        console.error("❌ No email found in URL params");
        setError("Invalid reset link - missing email parameter");
      }
    } else {
      console.error("❌ No access token or reset token found");
      setError("Invalid link");
    }

    setHasProcessedHash(true);
  }, [location.hash, hasProcessedHash]);

  const handleSetPassword = async () => {
    console.log("🔐 Starting password set process");
    // Clear any previous errors
    setError("");

    // Validate inputs
    if (!email) {
      console.error("❌ No email found");
      setError(
        "Invalid link - email not found. Please use the reset link from your email."
      );
      return;
    }

    if (!password || !confirmPassword) {
      console.error("❌ Missing password fields");
      setError("Please fill in all fields");
      return;
    }

    // Validate password strength
    if (password.length < 6) {
      console.error("❌ Password too short");
      setError("Password must be at least 6 characters long");
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      console.error("❌ Passwords do not match");
      setError("Passwords do not match");
      return;
    }

    console.log(
      "✅ All validations passed, setting password for email:",
      email
    );
    setIsSettingPassword(true);

    try {
      console.log("📡 Calling setPassword mutation");
      const payload: any = {
        email,
        password,
      };

      if (token) {
        payload.token = token;
      }
      if (linkAccessToken) {
        payload.access_token = linkAccessToken;
      }

      await setPassword(payload);

      console.log("✅ Password set successfully");
      // On success, navigate to login with a success message
      navigate("/login", {
        state: { message: "Password set successfully. Please log in." },
      });
    } catch (err) {
      console.error("❌ Error setting password:", err);
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred while setting password"
      );
      setIsSettingPassword(false);
    }
  };

  return (
    <View
      style={{
        flex: 1,
        flexDirection: isMobile ? "column" : "row",
        marginTop: isMobile ? 12.5 : undefined,
      }}
    >
      {/* Left Side - Set Password Form */}
      <View
        style={{
          flex: isMobile ? undefined : 0.6,
          width: isMobile ? "100%" : undefined,
          backgroundColor: colors.gray[100],
          padding: isMobile ? spacing.lg : spacing.xxl,
          alignItems: "center",
          justifyContent: "center",
          boxShadow: isMobile
            ? undefined
            : "inset -1px 0 0 0 rgba(0, 0, 0, 0.05), inset -2px 0 0 0 rgba(0, 0, 0, 0.03)",
        }}
      >
        {/* Logo on mobile */}
        {isMobile && (
          <View style={{ marginBottom: spacing.xl, alignSelf: "center" }}>
            <img
              src="/transparent-logo.png"
              alt="Money Personality Logo"
              style={{
                height: 108,
                width: "auto",
                cursor: "pointer",
                objectFit: "contain",
                padding: 24,
              }}
              onClick={() => navigate("/")}
            />
          </View>
        )}
        {/* Form Content */}
        <View style={{ width: "100%", maxWidth: isMobile ? 340 : 600 }}>
          <CustomText
            style={{
              fontSize: isMobile ? 30 : 60,
              fontWeight: "400",
              marginBottom: isMobile ? spacing.xs : spacing.sm,
              fontFamily: "Nunito",
              textAlign: isMobile ? "center" : undefined,
            }}
          >
            Reset Your Password
          </CustomText>

          <CustomText
            style={{
              fontSize: isMobile ? 18 : 24,
              color: colors.gray[600],
              marginBottom: isMobile ? spacing.md : spacing.lg,
              fontFamily: "Nunito",
              textAlign: isMobile ? "center" : undefined,
            }}
          >
            Please enter a new password for your account
          </CustomText>

          {/* Account Information Section */}
          <View style={{ marginBottom: spacing.xl }}>
            <View style={{ marginBottom: spacing.md }}>
              <CustomText
                style={{
                  marginBottom: spacing.sm,
                  color: colors.dark,
                  fontFamily: "Nunito",
                  fontSize: 12,
                }}
              >
                Email
              </CustomText>
              <TextInput
                value={email || ""}
                disabled
                style={{
                  height: 40,
                  borderWidth: 1,
                  borderColor: colors.gray[200],
                  borderRadius: 6,
                  paddingHorizontal: 12,
                  outlineStyle: "none",
                  width: "100%",
                  fontFamily: "Nunito",
                  fontSize: 14,
                  backgroundColor: colors.gray[100],
                  color: colors.gray[600],
                }}
              />
            </View>

            <View style={{ marginBottom: spacing.md }}>
              <CustomText
                style={{
                  marginBottom: spacing.sm,
                  color: colors.dark,
                  fontFamily: "Nunito",
                  fontSize: 12,
                }}
              >
                New Password
              </CustomText>
              <View
                style={{
                  position: "relative",
                  width: "100%",
                }}
              >
                <TextInput
                  value={password}
                  onChangeText={setPasswordValue}
                  placeholder="********"
                  placeholderTextColor={colors.gray[500]}
                  secureTextEntry={!showPassword}
                  style={{
                    height: 40,
                    borderWidth: 1,
                    borderColor: colors.gray[200],
                    borderRadius: 6,
                    paddingHorizontal: 12,
                    paddingRight: 40,
                    outlineStyle: "none",
                    width: "100%",
                    fontFamily: "Nunito",
                    fontSize: 14,
                    backgroundColor: colors.white,
                  }}
                />
                <Pressable
                  onPress={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: 12,
                    top: 7,
                    padding: 4,
                  }}
                >
                  <SvgEye
                    width={20}
                    height={20}
                    color={colors.gray[500]}
                    isOpen={showPassword}
                  />
                </Pressable>
              </View>
            </View>

            <View style={{ marginBottom: spacing.md }}>
              <CustomText
                style={{
                  marginBottom: spacing.sm,
                  color: colors.dark,
                  fontFamily: "Nunito",
                  fontSize: 12,
                }}
              >
                Confirm Password
              </CustomText>
              <View
                style={{
                  position: "relative",
                  width: "100%",
                }}
              >
                <TextInput
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  placeholder="********"
                  placeholderTextColor={colors.gray[500]}
                  secureTextEntry={!showConfirmPassword}
                  onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (
                      e.key === "Enter" &&
                      !(!password || !confirmPassword || isSettingPassword)
                    ) {
                      handleSetPassword();
                    }
                  }}
                  style={{
                    height: 40,
                    borderWidth: 1,
                    borderColor: colors.gray[200],
                    borderRadius: 6,
                    paddingHorizontal: 12,
                    paddingRight: 40,
                    outlineStyle: "none",
                    width: "100%",
                    fontFamily: "Nunito",
                    fontSize: 14,
                    backgroundColor: colors.white,
                  }}
                />
                <Pressable
                  onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: 12,
                    top: 7,
                    padding: 4,
                  }}
                >
                  <SvgEye
                    width={20}
                    height={20}
                    color={colors.gray[500]}
                    isOpen={showConfirmPassword}
                  />
                </Pressable>
              </View>
            </View>
          </View>

          {error && (
            <CustomText
              style={{
                color: colors.danger,
                marginTop: spacing.sm,
                fontSize: isMobile ? 14 : 18,
                fontFamily: "Nunito",
                marginBottom: 10,
                paddingLeft: 4,
              }}
            >
              {error}
            </CustomText>
          )}

          <View style={{ marginBottom: spacing.xl }}>
            <Button
              onPress={handleSetPassword}
              label="Reset Password"
              loading={isSettingPassword}
              disabled={!password || !confirmPassword || isSettingPassword}
              rounded={false}
              style={{
                width: "100%",
                height: 40,
              }}
              textStyle={{
                fontSize: 14,
                fontFamily: "Nunito",
              }}
            />
          </View>
        </View>
      </View>

      {/* Right Side - Testimonial */}
      <View
        style={{
          flex: isMobile ? undefined : 0.4,
          width: isMobile ? "100%" : undefined,
          backgroundColor: colors.white,
          padding: isMobile ? spacing.lg : spacing.xxl,
          alignItems: isMobile ? "center" : undefined,
          height: isMobile ? "100%" : undefined,
        }}
      >
        {/* Logo only on desktop */}
        {!isMobile && (
          <View style={{ marginBottom: spacing.xxl, alignSelf: "flex-end" }}>
            <img
              src="/transparent-logo.png"
              alt="Money Personality Logo"
              style={{
                height: 60,
                width: "auto",
                cursor: "pointer",
                objectFit: "contain",
              }}
              onClick={() => navigate("/")}
            />
          </View>
        )}

        <View
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: isMobile ? spacing.lg : 100,
          }}
        >
          <View style={{ maxWidth: 420 }}>
            <CustomText
              style={{
                fontSize: 28,
                lineHeight: 42,
                color: colors.gray[900],
                marginBottom: spacing.xl,
                fontFamily: "Nunito",
                textAlign: isMobile ? "center" : undefined,
              }}
            >
              "I can't believe how accurate the Money Personality test is...it
              felt like someone had been watching my relationship with money my
              entire life."
            </CustomText>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: isMobile ? "center" : undefined,
              }}
            >
              <img
                src={require("../components/img/ben-pic.jpeg")}
                alt="Ben's profile"
                style={{
                  width: isMobile ? 75 : 100,
                  height: "auto",
                  borderRadius: 100,
                  objectFit: "cover",
                  marginRight: spacing.md,
                }}
              />
              <CustomText
                style={{
                  fontSize: 18,
                  color: colors.gray[600],
                  fontFamily: "Nunito",
                }}
              >
                Ben Barton, Entrepreneur
              </CustomText>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}
